import {
  collection,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc,
  Firestore
} from 'firebase/firestore';

export interface DetailedCallLog {
  id: string;
  userId: string;
  userName?: string;
  leadUid: string;
  leadName?: string;
  phoneNumber?: string;
  type: 'OUTGOING' | 'INCOMING' | 'MISSED';
  duration: number;
  callTimestamp: Timestamp;
  notes?: string;
  recording?: string;
  outcomeUid?: string;
  outcomeName?: string;
}

export interface CallLogFilters {
  companyId: string;
  startTime: string | null;
  endTime: string | null;
  userIds?: string[];
  callType: 'OUTGOING' | 'INCOMING' | 'MISSED' | 'ATTEMPTED' | 'CONNECTED' | 'RECEIVED';
}

export class DetailedCallLogService {
  static async fetchDetailedCallLogs(
    db: Firestore,
    filters: CallLogFilters
  ): Promise<DetailedCallLog[]> {
    if (!filters.companyId) return [];

    try {
      const callLogsRef = collection(db, `companies/${filters.companyId}/callLogs`);
      
      // Build query based on filters
      let callLogsQuery = query(callLogsRef);

      // Build query based on call type first, then add other filters
      switch (filters.callType) {
        case 'OUTGOING':
        case 'ATTEMPTED':
          callLogsQuery = query(callLogsQuery, where('type', '==', 'OUTGOING'));
          break;
        case 'INCOMING':
        case 'RECEIVED':
          callLogsQuery = query(callLogsQuery, where('type', '==', 'INCOMING'));
          break;
        case 'MISSED':
          callLogsQuery = query(callLogsQuery, where('type', '==', 'MISSED'));
          break;
        case 'CONNECTED':
          // For connected calls, we'll filter after fetching to avoid complex query
          callLogsQuery = query(callLogsQuery, where('type', '==', 'OUTGOING'));
          break;
      }

      // Add time range filter
      if (filters.startTime && filters.endTime) {
        const startDate = new Date(filters.startTime);
        const endDate = new Date(filters.endTime);
        callLogsQuery = query(
          callLogsQuery,
          where('callTimestamp', '>=', Timestamp.fromDate(startDate)),
          where('callTimestamp', '<=', Timestamp.fromDate(endDate))
        );
      }

      // Add user filter - only add if we want to filter by specific users
      // If userIds is undefined or empty, we'll show ALL staff (don't add any user filter)
      if (filters.userIds && filters.userIds.length > 0) {
        console.log(`Filtering by ${filters.userIds.length} specific users`);
        if (filters.userIds.length === 1) {
          callLogsQuery = query(callLogsQuery, where('userId', '==', filters.userIds[0]));
        } else if (filters.userIds.length <= 10) {
          // Firebase 'in' query supports up to 10 items
          callLogsQuery = query(callLogsQuery, where('userId', 'in', filters.userIds));
        }
        // If more than 10 users, we'll filter in memory after fetching
      } else {
        console.log('No user filter applied - showing ALL staff data');
      }

      // Add ordering
      callLogsQuery = query(callLogsQuery, orderBy('callTimestamp', 'desc'));

      const callLogsSnapshot = await getDocs(callLogsQuery);
      let callLogsData: DetailedCallLog[] = [];

      // Cache for users and leads to avoid repeated fetches
      const userCache = new Map<string, string>();
      const leadCache = new Map<string, { name: string; phoneNumber: string }>();
      const outcomeCache = new Map<string, string>();

      for (const callLogDoc of callLogsSnapshot.docs) {
        const callLogData = callLogDoc.data();
        
        // Apply additional filtering for connected calls and user filtering if needed
        if (filters.callType === 'CONNECTED' && (!callLogData.duration || callLogData.duration <= 0)) {
          continue; // Skip non-connected calls
        }

        // Apply user filtering if more than 10 users (couldn't use 'in' query)
        // or if we want to filter by specific users from a large set
        if (filters.userIds && filters.userIds.length > 10) {
          if (!filters.userIds.includes(callLogData.userId)) {
            continue; // Skip calls from users not in the filter
          }
        }
        
        // Fetch user name if not cached
        let userName = 'Unknown User';
        if (callLogData.userId && !userCache.has(callLogData.userId)) {
          try {
            const userDoc = await getDoc(doc(db, 'users', callLogData.userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              userName = userData.name || userData.email || 'Unknown User';
              userCache.set(callLogData.userId, userName);
            }
          } catch (error) {
            console.error('Error fetching user name:', error);
          }
        } else {
          userName = userCache.get(callLogData.userId) || 'Unknown User';
        }

        // Fetch lead name and phone if not cached
        let leadName = 'Unknown Lead';
        let phoneNumber = '';
        if (callLogData.leadUid && !leadCache.has(callLogData.leadUid)) {
          try {
            const leadDoc = await getDoc(doc(db, `companies/${filters.companyId}/leads`, callLogData.leadUid));
            if (leadDoc.exists()) {
              const leadData = leadDoc.data();
              leadName = leadData.name || leadData.studentName || 'Unknown Lead';
              phoneNumber = leadData.phoneNumber || '';
              leadCache.set(callLogData.leadUid, { name: leadName, phoneNumber });
            }
          } catch (error) {
            console.error('Error fetching lead name:', error);
          }
        } else {
          const cached = leadCache.get(callLogData.leadUid);
          leadName = cached?.name || 'Unknown Lead';
          phoneNumber = cached?.phoneNumber || '';
        }

        // Fetch outcome name if not cached
        let outcomeName = undefined;
        if (callLogData.outcomeUid && !outcomeCache.has(callLogData.outcomeUid)) {
          try {
            const outcomeDoc = await getDoc(doc(db, 'outcomes', callLogData.outcomeUid));
            if (outcomeDoc.exists()) {
              const outcomeData = outcomeDoc.data();
              outcomeName = outcomeData.name || 'Unknown Outcome';
              outcomeCache.set(callLogData.outcomeUid, outcomeName);
            }
          } catch (error) {
            console.error('Error fetching outcome name:', error);
          }
        } else {
          outcomeName = outcomeCache.get(callLogData.outcomeUid);
        }

        callLogsData.push({
          id: callLogDoc.id,
          userId: callLogData.userId,
          userName,
          leadUid: callLogData.leadUid || '',
          leadName,
          phoneNumber,
          type: callLogData.type || 'OUTGOING',
          duration: callLogData.duration || 0,
          callTimestamp: callLogData.callTimestamp,
          notes: callLogData.notes,
          recording: callLogData.recording,
          outcomeUid: callLogData.outcomeUid,
          outcomeName
        });
      }

      console.log(`Fetched ${callLogsData.length} call logs for ${filters.callType}`, {
        totalRecords: callLogsData.length,
        uniqueUsers: new Set(callLogsData.map(log => log.userId)).size,
        filterApplied: {
          callType: filters.callType,
          userIds: filters.userIds?.length || 0,
          isAllUsers: !filters.userIds || filters.userIds.length === 0,
          hasTimeFilter: !!(filters.startTime && filters.endTime)
        },
        sampleData: callLogsData.slice(0, 3).map(log => ({
          userId: log.userId,
          userName: log.userName,
          type: log.type,
          duration: log.duration
        }))
      });
      return callLogsData;
    } catch (error) {
      console.error('Error fetching detailed call logs:', error);
      console.error('Filters used:', filters);
      
      // Log additional debug information
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      
      return [];
    }
  }

  // Helper method to format call duration
  static formatDuration(duration: number): string {
    if (duration === 0) return '0s';
    
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Helper method to format timestamp
  static formatTimestamp(timestamp: Timestamp): string {
    return timestamp.toDate().toLocaleString();
  }

  // Helper method to get call type display
  static getCallTypeDisplay(type: string): { label: string; color: string } {
    switch (type) {
      case 'OUTGOING':
        return { label: 'Outgoing', color: 'text-blue-600 bg-blue-50' };
      case 'INCOMING':
        return { label: 'Incoming', color: 'text-green-600 bg-green-50' };
      case 'MISSED':
        return { label: 'Missed', color: 'text-red-600 bg-red-50' };
      default:
        return { label: 'Call', color: 'text-gray-600 bg-gray-50' };
    }
  }
}
